import type { ChangeEvent, ReactNode } from 'react'
import { useCallback, useState } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import SearchIcon from '@/assets/icons/icon-search.svg'
import { classNames } from '@/utils/helpers'

import { ErrorText } from '../error-text.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select.tsx'
import { Skeleton } from '../skeleton.tsx'
import { Tooltip } from '../tooltip.tsx'

type OptionLabelType = string | JSX.Element

export type SelectOptionsType<L extends OptionLabelType = string> = Array<{
  label: L
  value: string
  disabled?: boolean
  forcedSelected?: boolean
  tooltip?: string
}>

interface FormSelectProps<L extends OptionLabelType> {
  name: string
  label?: ReactNode
  description?: string
  options: Array<string> | SelectOptionsType<L>
  className?: string
  placeholder?: string
  disabled?: boolean
  contentClassName?: string
  labelTextClassName?: string
  descriptionTextClassName?: string
  onChange?: (value: string) => void
  showError?: boolean
  isLoading?: boolean
  id?: string
  searchable?: boolean
  triggerTestId?: string
  contentTestId?: string
}

export const FormSelect = <L extends OptionLabelType = string>({
  name,
  label,
  description,
  options,
  disabled,
  className,
  placeholder,
  contentClassName,
  labelTextClassName,
  descriptionTextClassName,
  onChange,
  showError,
  isLoading,
  id,
  searchable,
  triggerTestId,
  contentTestId,
}: FormSelectProps<L>) => {
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({ name, control })
  const [searchValue, setSearchValue] = useState('')
  const fieldValue = field.value || undefined
  const [isOpen, setIsOpen] = useState(false)

  const handleChange = (value: string) => {
    field.onChange(value)
    onChange?.(value)
  }

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
      setTimeout(() => event.target.focus(), 0)
    },
    [],
  )

  return (
    <>
      <div className={classNames('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            className={classNames(
              'text-text-default text-sm font-medium',
              labelTextClassName,
            )}
          >
            {label}
          </label>
        )}

        {description && (
          <div
            className={classNames(
              'text-secondary-300 text-xs font-normal mb-1',
              descriptionTextClassName,
            )}
          >
            {description}
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <Select
            onValueChange={handleChange}
            value={fieldValue}
            disabled={disabled}
            open={isOpen}
            onOpenChange={setIsOpen}
          >
            <SelectTrigger
              id={id}
              className={classNames(
                'text-secondary-500 w-full text-base',
                contentClassName,
              )}
              testId={triggerTestId}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent
              className="max-h-[250px]"
              testId={contentTestId}
            >
              {searchable && (
                <div className="mb-[10px] flex items-center border-b px-3">
                  <SearchIcon className="text-secondary-500 ml-[2px] mr-[10px] h-3 w-3 shrink-0 dark:text-white" />
                  <input
                    autoFocus
                    placeholder="Search"
                    value={searchValue}
                    onChange={handleSearchChange}
                    className="placeholder:text-muted-foreground h-10 w-full py-3 text-sm outline-none dark:bg-inherit dark:text-white dark:caret-white"
                  />
                </div>
              )}
              {options.length > 0 ? (
                <SelectGroup className="max-h-[300px] overflow-auto">
                  {options.map((option) => {
                    const { label, value, disabled, tooltip } =
                      typeof option === 'object'
                        ? option
                        : {
                            label: option,
                            value: option,
                            disabled: false,
                            tooltip: undefined,
                          }
                    const isFilteredOut =
                      searchValue &&
                      (typeof label !== 'string' ||
                        !label
                          .toLowerCase()
                          .includes(searchValue.toLowerCase()))
                    const itemContent = (
                      <SelectItem
                        key={value}
                        value={value}
                        disabled={disabled}
                        className={classNames('text-secondary-500 text-sm', {
                          hidden: isFilteredOut,
                        })}
                      >
                        {label}
                      </SelectItem>
                    )

                    return tooltip ? (
                      <Tooltip
                        content={tooltip}
                        key={value}
                        className="block cursor-default"
                      >
                        {itemContent}
                      </Tooltip>
                    ) : (
                      itemContent
                    )
                  })}
                </SelectGroup>
              ) : (
                <SelectGroup className="max-h-[300px] overflow-auto">
                  <div className="flex items-center justify-center p-4">
                    <p className="text-secondary-500 text-sm">No options</p>
                  </div>
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        )}
      </div>
      {showError && error && <ErrorText>{error?.message}</ErrorText>}
    </>
  )
}
